import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useDebounce } from '../../../hooks/useDebounce';
import styles from './SearchBar.module.css';

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Debounce delay in ms. Defaults to 300. */
  debounceMs?: number;
  /** Optional aria-label override. */
  ariaLabel?: string;
  className?: string;
  autoFocus?: boolean;
}

/**
 * Controlled search input. The `value` prop is treated as the canonical
 * source — typing updates an internal local state for instant feedback,
 * and the debounced value is reported back via onChange after `debounceMs`.
 */
export function SearchBar({
  value,
  onChange,
  placeholder = 'Search…',
  debounceMs = 300,
  ariaLabel = 'Search',
  className,
  autoFocus,
}: SearchBarProps) {
  const [local, setLocal] = useState(value);
  const lastEmitted = useRef(value);

  // Sync external value changes back into the local state.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      setLocal(value);
      lastEmitted.current = value;
    }
  }, [value]);

  const debounced = useDebounce(local, debounceMs);

  useEffect(() => {
    if (debounced !== lastEmitted.current) {
      lastEmitted.current = debounced;
      onChange(debounced);
    }
  }, [debounced, onChange]);

  return (
    <div className={cn(styles.wrapper, className)}>
      <Search size={16} aria-hidden className={styles.icon} />
      <input
        type="search"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={styles.input}
        autoFocus={autoFocus}
      />
      {local.length > 0 ? (
        <button
          type="button"
          onClick={() => {
            setLocal('');
          }}
          className={styles.clear}
          aria-label="Clear search"
        >
          <X size={14} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
